<template>
<div class="dock-control-panel" :class="{ 'dock-control-panel--debugging': debugStatus }">
  <!-- title -->
  <div class="dock-control-panel-header">
    <span class="dock-control-panel-title">信息详情 <span class="dock-control-panel-chevron">»</span><span class="dock-control-panel-sn">{{ props.sn}}</span></span>
    <button type="button" class="dock-control-panel-close drag-ignore" title="关闭信息详情" aria-label="关闭信息详情" @click="closeControlPanel">
      <CloseOutlined />
    </button>
  </div>
  <!-- setting -->
  <DeviceSettingBox :sn="props.sn" :deviceInfo="props.deviceInfo"></DeviceSettingBox>
  <!-- cmd -->
    <div class="control-cmd-wrapper">
    <div class="control-cmd-header" :class="{ 'control-cmd-header--active': debugStatus }">
      <span>远程调试</span>
      <a-switch class="debug-btn" aria-label="远程调试" checked-children="开" un-checked-children="关" v-model:checked="debugStatus" @change="onDeviceStatusChange"></a-switch>
    </div>
    <div class="control-cmd-box">
      <a-tooltip v-for="(cmdItem, index) in cmdList" :key="cmdItem.cmdKey" placement="top">
        <template #title>
          <div><strong>{{ cmdItem.label }}</strong></div>
          <div>{{ cmdItem.description }}</div>
        </template>
        <div class="control-cmd-item">
          <div class="control-cmd-item-copy">
            <span class="control-cmd-item-icon"><component :is="getCmdIcon(cmdItem.cmdKey)" /></span>
            <div class="control-cmd-item-left">
              <div class="item-label">{{ cmdItem.label }}</div>
              <div class="item-status">{{ cmdItem.status }}</div>
            </div>
          </div>
          <div class="control-cmd-item-right">
            <a-button :disabled="(cmdItem.cmdKey !== DeviceCmd.DroneOpen && !debugStatus) || cmdItem.disabled" :loading="cmdItem.loading" size="small" type="primary" @click="sendControlCmd(cmdItem, index)">
              {{ cmdItem.operateText }}
            </a-button>
          </div>
        </div>
      </a-tooltip>
    </div>
    <div class="control-cmd-speaker">
      <SpeakerControlPanel
        :dock-sn="props.sn"
        :source="props.deviceInfo.device"
        :enabled="debugStatus"
        :stop-enabled="debugStatus"
        disabled-reason="请先开启机场远程调试"
      />
    </div>
    <!-- 日志查看/导出不受远程调试开关限制 -->
    <div class="control-cmd-logs">
      <DockLogPanel :sn="props.sn" />
    </div>
  </div>
</div>

</template>

<script setup lang="ts">
import { defineProps, defineEmits, ref, watch } from 'vue'
import { Modal } from 'ant-design-vue'
import {
  AlertOutlined,
  ExclamationCircleOutlined,
  BulbOutlined,
  CloseOutlined,
  ColumnWidthOutlined,
  DatabaseOutlined,
  InboxOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  WifiOutlined
} from '@ant-design/icons-vue'
import { useDockControl } from './use-dock-control'
import { DeviceInfoType, EDockModeCode } from '/@/types/device'
import { cmdList as baseCmdList, DeviceCmd, DeviceCmdItem } from '/@/types/device-cmd'
import { useMyStore } from '/@/store'
import { updateDeviceCmdInfoByOsd, updateDeviceCmdInfoByExecuteInfo, updateDeviceSingleCmdInfo } from '/@/utils/device-cmd'
import DeviceSettingBox from './DeviceSettingBox.vue'
import SpeakerControlPanel from './SpeakerControlPanel.vue'
import DockLogPanel from './DockLogPanel.vue'
import { COVER_FORCE_CLOSE_CONFIRM_TEXT } from '/@/utils/dock-command-feedback'

const props = defineProps<{
  sn: string,
  deviceInfo: DeviceInfoType,
}>()

const store = useMyStore()
const initCmdList = baseCmdList.map(cmdItem => Object.assign({}, cmdItem))
const cmdList = ref(initCmdList)
const cmdIcons: Partial<Record<DeviceCmd, any>> = {
  [DeviceCmd.DeviceReboot]: ReloadOutlined,
  [DeviceCmd.DroneOpen]: PoweroffOutlined,
  [DeviceCmd.DroneClose]: PoweroffOutlined,
  [DeviceCmd.CoverOpen]: InboxOutlined,
  [DeviceCmd.CoverClose]: InboxOutlined,
  [DeviceCmd.CoverForceClose]: ExclamationCircleOutlined,
  [DeviceCmd.PutterOpen]: ColumnWidthOutlined,
  [DeviceCmd.PutterClose]: ColumnWidthOutlined,
  [DeviceCmd.ChargeOpen]: ThunderboltOutlined,
  [DeviceCmd.ChargeClose]: ThunderboltOutlined,
  [DeviceCmd.DeviceFormat]: DatabaseOutlined,
  [DeviceCmd.DroneFormat]: DatabaseOutlined,
  [DeviceCmd.SupplementLightOpen]: BulbOutlined,
  [DeviceCmd.SupplementLightClose]: BulbOutlined,
  [DeviceCmd.AlarmStateSwitch]: AlertOutlined,
  [DeviceCmd.BatteryStoreModeSwitch]: ToolOutlined,
  [DeviceCmd.DroneBatteryModeSwitch]: ToolOutlined,
  [DeviceCmd.SdrWorkModeSwitch]: WifiOutlined,
}

function getCmdIcon (cmd: DeviceCmd) {
  return cmdIcons[cmd] || ToolOutlined
}

// 根据机场指令执行状态更新信息
watch(() => store.state.devicesCmdExecuteInfo, (devicesCmdExecuteInfo) => {
  if (props.sn && devicesCmdExecuteInfo[props.sn]) {
    updateDeviceCmdInfoByExecuteInfo(cmdList.value, devicesCmdExecuteInfo[props.sn])
  }
}, {
  immediate: true,
  deep: true,
})

// 根据设备osd信息更新信息
watch(() => props.deviceInfo, (value) => {
  updateDeviceCmdInfoByOsd(cmdList.value, value)
  // console.log('deviceInfo', value)
}, {
  immediate: true,
  deep: true
})

// dock 控制指令
const debugStatus = ref(false)
const emit = defineEmits(['close-control-panel'])

watch(() => props.deviceInfo.dock?.basic_osd?.mode_code, (modeCode) => {
  debugStatus.value = modeCode === EDockModeCode.Remote_Debugging
}, {
  immediate: true
})

function closeControlPanel () {
  emit('close-control-panel', props.sn, debugStatus.value)
}

async function onDeviceStatusChange (status: boolean) {
  let result = false
  if (status) {
    result = await dockDebugOnOff(props.sn, true)
  } else {
    result = await dockDebugOnOff(props.sn, false)
  }
  if (!result) {
    if (status) {
      debugStatus.value = false
    } else {
      debugStatus.value = true
    }
  }
}

const {
  sendDockControlCmd,
  dockDebugOnOff
} = useDockControl()

async function sendControlCmd (cmdItem: DeviceCmdItem, index: number) {
  if (cmdItem.cmdKey === DeviceCmd.CoverForceClose && !await confirmCoverForceClose()) {
    return
  }
  const success = await sendDockControlCmd({
    sn: props.sn,
    cmd: cmdItem.cmdKey,
    action: cmdItem.action
  }, true)
  if (success) {
    updateDeviceSingleCmdInfo(cmdList.value[index])
  }
}

// DJI's precondition for cover_force_close is a human camera check, so it is
// never sent from a bare click.
function confirmCoverForceClose (): Promise<boolean> {
  return new Promise(resolve => {
    Modal.confirm({
      title: '强制关闭舱盖',
      content: COVER_FORCE_CLOSE_CONFIRM_TEXT,
      okText: '已确认舱内无飞行器，强制关闭',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })
}

</script>

<style lang='scss' scoped>
.dock-control-panel{
  position: absolute;
  left: auto;
  right: calc(100% + 64px);
  top: 0px;
  width: 390px;
  box-sizing: border-box;
  padding: 0 !important;
  background: #3c3c3c;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  max-height: calc(100vh - 96px);
  overflow-x: hidden;
  overflow-y: auto;

  .dock-control-panel-header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 38px;
    padding: 0 12px;
    color: #fff;
    background: #202020;
    border-bottom: 1px solid #4a4a4a;
    user-select: none;
  }

  .dock-control-panel-title{
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 700;
  }

  .dock-control-panel-chevron{
    color: #d6d6d6;
    font-size: 15px;
    letter-spacing: -2px;
  }

  .dock-control-panel-sn{
    min-width: 0;
    max-width: 158px;
    color: #8e9499;
    font-size: 10px;
    font-weight: 400;
    font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dock-control-panel-close{
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    color: #fff;
    font-size: 16px;
    line-height: 1;
    border-radius: 2px;
    background: transparent;
    border: 0;
    cursor: pointer;
  }

  .dock-control-panel-close:hover{
    background: rgba(255, 255, 255, 0.12);
  }

  .control-cmd-wrapper{
    background: #3c3c3c;
    border-top: 6px solid #202020;

    .control-cmd-header{
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 36px;
      padding: 0 8px;
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);

      &--active{
        color: #fff;
      }

      .debug-btn{
        margin-left: 0;
      }
    }

    .control-cmd-box{
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;
      padding: 7px 8px 10px;
      .control-cmd-item{
        min-width: 0;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 5px;
        background: #3d3d3d;
        border: 1px solid #666;
        border-radius: 1px;
        padding: 0 6px;

        .control-cmd-item-copy{
          flex: 1 1 auto;
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
        }

        .control-cmd-item-icon{
          display: inline-flex;
          flex: 0 0 22px;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 21px;
        }

        .control-cmd-item-left{
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          min-width: 0;

          .item-label{
            color: #fff;
            font-size: 12px;
            font-weight: 700;
            line-height: 17px;
            white-space: nowrap;
          }

          .item-status{
            overflow: hidden;
            color: #a9a9a9;
            font-size: 10px;
            line-height: 15px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .control-cmd-item-right{
          flex: 0 0 auto;
        }
      }
    }

    .control-cmd-speaker{
      padding: 0 8px 10px;
    }

    .control-cmd-logs{
      padding: 0 8px 10px;
    }
  }

  :deep(.ant-switch){
    min-width: 40px;
    height: 20px;
    line-height: 20px;
    background: #74787b;
    border: 0;
    box-shadow: none;
  }

  :deep(.ant-switch-checked){
    background: #168bff;
  }

  :deep(.ant-switch-handle){
    top: 2px;
    width: 16px;
    height: 16px;
  }

  :deep(.ant-btn-sm){
    height: 24px;
    padding: 0 7px;
    font-size: 12px;
    line-height: 22px;
    border-radius: 2px;
  }

  :deep(.ant-btn-primary){
    color: #fff;
    background: #62676b;
    border-color: #62676b;
    box-shadow: none;
  }

  :deep(.ant-btn-primary:not([disabled]):hover),
  :deep(.ant-btn-primary:not([disabled]):focus){
    background: #168bff;
    border-color: #168bff;
  }

  :deep(.ant-btn-primary[disabled]){
    color: rgba(255, 255, 255, 0.38);
    background: #5b5e61;
    border-color: #5b5e61;
  }
}
</style>
